import { useEffect, useRef, useState, useCallback } from "react";

interface SwipeOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  threshold?: number; // minimum distance in pixels
  edgeOnly?: boolean; // only trigger from edge of screen
  edgeWidth?: number; // width of edge zone in pixels
}

interface SwipeState {
  isSwiping: boolean;
  swipeProgress: number; // 0 to 1, how far the swipe has progressed
  direction: "left" | "right" | null;
}

export function useSwipe({
  onSwipeLeft,
  onSwipeRight,
  threshold = 50,
  edgeOnly = false,
  edgeWidth = 30,
}: SwipeOptions): SwipeState {
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const isEdgeSwipe = useRef<boolean>(false);
  
  const [swipeState, setSwipeState] = useState<SwipeState>({
    isSwiping: false,
    swipeProgress: 0,
    direction: null,
  });

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      const touch = e.touches[0];
      touchStartX.current = touch.clientX;
      touchStartY.current = touch.clientY;
      isEdgeSwipe.current = touch.clientX <= edgeWidth;
    },
    [edgeWidth]
  );

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (touchStartX.current === null) return;
    
    const currentX = e.touches[0].clientX;
    const deltaX = currentX - touchStartX.current;
    
    // Only show indicator for edge swipes going right
    if (isEdgeSwipe.current && deltaX > 10) {
      const progress = Math.min(deltaX / (threshold * 2), 1);
      setSwipeState({
        isSwiping: true,
        swipeProgress: progress,
        direction: "right",
      });
    } else if (deltaX < -10) {
      const progress = Math.min(Math.abs(deltaX) / (threshold * 2), 1);
      setSwipeState({
        isSwiping: true,
        swipeProgress: progress,
        direction: "left",
      });
    }
  }, [threshold]);

  const handleTouchEnd = useCallback(() => {
    if (touchStartX.current === null) {
      return;
    }

    const startX = touchStartX.current;
    const { swipeProgress, direction } = swipeState;

    // Trigger action if swipe was far enough
    if (swipeProgress >= 0.5) {
      if (direction === "right" && onSwipeRight) {
        if (!edgeOnly || isEdgeSwipe.current) {
          onSwipeRight();
        }
      }
      if (direction === "left" && onSwipeLeft) {
        onSwipeLeft();
      }
    }

    // Reset state
    touchStartX.current = null;
    isEdgeSwipe.current = false;
    setSwipeState({
      isSwiping: false,
      swipeProgress: 0,
      direction: null,
    });
  }, [swipeState, edgeOnly, onSwipeLeft, onSwipeRight]);

  useEffect(() => {
    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchmove", handleTouchMove, { passive: true });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });
    document.addEventListener("touchcancel", handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return swipeState;
}
